import { EnvironmentInjector, EventEmitter, Injectable, Type } from '@angular/core';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ModalCloseType } from '../components/modal/modal-close-type.enum';
import { BaseModalDialogDirective } from '../directives/base-modal-dialog.directive';
import { ModalSize } from '../enums/modal-size.enum';
import { GenericModalWrapperComponent } from '../components/_modals/generic-modal-wrapper/generic-modal-wrapper.component';

@Injectable({ providedIn: 'root' })
export class ModalService {
  private openModals: NgbModalRef[] = [];

  constructor(private ngbModal: NgbModal) {
    this.ngbModal.activeInstances.subscribe(modals => this.openModals = modals);
  }

  open<TModalDialog extends BaseModalDialogDirective>(type: new (...args: never[]) => TModalDialog, setParameters?: (d: TModalDialog) => void, injector?: EnvironmentInjector): NgbModalRef {
    const modalRef = this.ngbModal.open(type, this.getModalOptions(type, injector));
    const component = modalRef.componentInstance as TModalDialog;
    if (setParameters) {
      setParameters(component);
    }
    return modalRef;
  }

  openSingle<TModalDialog extends BaseModalDialogDirective>(type: new (...args: never[]) => TModalDialog, setParameters?: (d: TModalDialog) => void): NgbModalRef {
    this.openModals.filter(m => m.componentInstance instanceof type).forEach(m => m.close());
    return this.open(type, setParameters);
  }

  openWithWrapper<TContent>(
    contentType: Type<TContent>,
    config: {
      title: string;
      size?: ModalSize;
      closeType?: ModalCloseType;
      modalClass?: string;
    },
    setContentParameters?: (content: TContent) => void,
  ): NgbModalRef {
    const size = config.size ?? ModalSize.Large;
    const closeType = config.closeType ?? ModalCloseType.Safe;

    // Build modal options directly with the config values
    const modalOptions = this.buildModalOptions(size, closeType);

    // Open modal with custom options
    const modalRef = this.ngbModal.open(GenericModalWrapperComponent, modalOptions);
    const wrapper = modalRef.componentInstance as GenericModalWrapperComponent;

    wrapper.modalTitle = config.title;
    wrapper.modalSize = size;
    wrapper.modalCloseType = closeType;
    if (config.modalClass) {
      wrapper.customModalClass = config.modalClass;
    }
    wrapper.contentComponent = contentType;

    // Build content inputs from parameters
    const contentInputs: Record<string, any> = {};

    // If setContentParameters is provided, create a temporary content instance to gather parameters
    if (setContentParameters) {
      const tempContent = {} as TContent;
      setContentParameters(tempContent);
      Object.assign(contentInputs, tempContent);
    }

    wrapper.contentInputs = contentInputs;

    return modalRef;
  }

  // when close event is emit, then close the modal
  onCloseEvent<T>(): EventEmitter<T> {
    const eventEmitter = new EventEmitter<T>();
    eventEmitter.subscribe(result => {
      const openModal = this.openModals.find(m => m.componentInstance instanceof GenericModalWrapperComponent);
      openModal?.close(result);
    });
    return eventEmitter;
  }

  private buildModalOptions(size: ModalSize, closeType: ModalCloseType): NgbModalOptions {
    const allowCloseByEscKey = closeType === ModalCloseType.Normal;
    const allowCloseByOutsideClick = closeType === ModalCloseType.Normal;
    const dialogClass = this.getDialogClass(size);

    return {
      backdrop: allowCloseByOutsideClick ? true : 'static',
      keyboard: allowCloseByEscKey,
      centered: true,
      scrollable: true,
      modalDialogClass: dialogClass,
    };
  }

  isSomeOpen(): boolean {
    return this.ngbModal.hasOpenModals();
  }

  private getModalOptions<TModalDialog extends BaseModalDialogDirective>(type: new (...args: never[]) => TModalDialog, injector?: EnvironmentInjector): NgbModalOptions {
    const closeType = type.prototype.closeType as ModalCloseType;
    const size = type.prototype.size as ModalSize;

    const allowCloseByEscKey = closeType === ModalCloseType.Normal;
    const allowCloseByOutsideClick = closeType === ModalCloseType.Normal;
    const dialogClass = this.getDialogClass(size);

    return {
      backdrop: allowCloseByOutsideClick ? true : 'static',
      keyboard: allowCloseByEscKey,
      centered: true,
      scrollable: true,
      modalDialogClass: dialogClass,
      injector,
    };
  }

  private getDialogClass(size: ModalSize): string {
    switch (size) {
      case ModalSize.Small:
        return 'modal-size-sm';
      case ModalSize.Medium:
        return 'modal-size-md';
      case ModalSize.Large:
        return 'modal-size-lg';
      case ModalSize.XLarge:
        return 'modal-size-xl';
      case ModalSize.XXLarge:
        return 'modal-size-xxl';
      default:
        return 'modal-size-lg';
    }
  }
}
